import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import { Category, Role } from "protos/robot_syncer/plant_profile";
import {
  ConvertCategoryDraft,
  useConvertFixedCategoriesForm,
} from "./useConvertFixedCategoriesForm";
import { CreateCategoryFormFields } from "../createCategory/CreateCategoryDialog";
import { type CreateCategoryFormValues } from "../createCategory/useCreateCategoryForm";
import { Form, Formik, FormikHelpers } from "formik";
import {
  resolveCategoryRole,
  translateCategoryName,
} from "portal/utils/categoryCollection/category";
import { Trans, useTranslation } from "react-i18next";
import { UnsavedPlantProfile } from "portal/utils/categoryCollection/plantProfile";
import React, { FC, useEffect, useState } from "react";

/** Step 0 = overview; 1..n = fixed categories; n+1 = conversion complete. */
const OVERVIEW_STEP = 0;

interface ConvertFixedCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  unsavedPlantProfile: UnsavedPlantProfile;
  customerUuid?: string;
  onConvert: (
    categories: Category[],
    categoryRoles: Record<string, Role>
  ) => void;
}

export const ConvertFixedCategoriesDialog: FC<
  ConvertFixedCategoriesDialogProps
> = ({ open, onClose, unsavedPlantProfile, customerUuid, onConvert }) => {
  const { t } = useTranslation();
  // Freeze the profile for the duration of the dialog so applying conversion
  // (which updates the parent draft) does not reset the completion step.
  const [profileSnapshot, setProfileSnapshot] = useState(unsavedPlantProfile);
  const [wasOpen, setWasOpen] = useState(open);

  const {
    almanacGroups,
    defaultAlmanacId,
    initialDrafts,
    isLoading,
    validationSchema,
    resolveDraft,
  } = useConvertFixedCategoriesForm(profileSnapshot, customerUuid);

  const [activeStep, setActiveStep] = useState(OVERVIEW_STEP);
  const [drafts, setDrafts] = useState<ConvertCategoryDraft[]>(initialDrafts);
  const [hasConverted, setHasConverted] = useState(false);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setProfileSnapshot(unsavedPlantProfile);
      setActiveStep(OVERVIEW_STEP);
      setDrafts(initialDrafts);
      setHasConverted(false);
    }
  }

  useEffect(() => {
    setActiveStep(OVERVIEW_STEP);
    setDrafts(initialDrafts);
    setHasConverted(false);
  }, [initialDrafts]);

  const completeStep = drafts.length + 1;
  const categoryStepIndex = activeStep - 1;
  const isOverview = activeStep === OVERVIEW_STEP;
  const isCompleteStep = activeStep === completeStep;
  const currentDraft =
    isOverview || isCompleteStep ? undefined : drafts[categoryStepIndex];
  const isLastCategoryStep = categoryStepIndex === drafts.length - 1;

  const applyConversion = (
    draftsToConvert: ConvertCategoryDraft[],
    helpers?: FormikHelpers<CreateCategoryFormValues>
  ): boolean => {
    const convertedById = new Map<string, { category: Category; role: Role }>();
    for (const draft of draftsToConvert) {
      const result = resolveDraft(draft);
      if (!result.ok) {
        helpers?.setFieldError(result.field, result.message);
        helpers?.setSubmitting(false);
        return false;
      }
      convertedById.set(draft.id, result);
    }

    // Keep categories that already had almanacs; replace only those converted.
    const categories: Category[] = [];
    const categoryRoles: Record<string, Role> = {};
    for (const category of profileSnapshot.categories) {
      const converted = convertedById.get(category.id);
      if (converted) {
        categories.push(converted.category);
        categoryRoles[converted.category.id] = converted.role;
        continue;
      }
      categories.push(category);
      categoryRoles[category.id] = resolveCategoryRole(
        category.id,
        category.name,
        profileSnapshot.categoryRoles
      );
    }
    onConvert(categories, categoryRoles);
    setHasConverted(true);
    return true;
  };

  const commitCategoryStep = (
    values: CreateCategoryFormValues,
    helpers: FormikHelpers<CreateCategoryFormValues>
  ): void => {
    if (!currentDraft) {
      return;
    }
    const nextDraft: ConvertCategoryDraft = {
      ...currentDraft,
      ...values,
      name: values.name.trim(),
    };
    const resolved = resolveDraft(nextDraft);
    if (!resolved.ok) {
      helpers.setFieldError(resolved.field, resolved.message);
      return;
    }

    const nextDrafts = drafts.map((draft, index) => {
      if (index === categoryStepIndex) {
        return nextDraft;
      }
      if (index > categoryStepIndex) {
        return {
          ...draft,
          sourceAlmanacId: nextDraft.sourceAlmanacId,
        };
      }
      return draft;
    });
    setDrafts(nextDrafts);

    if (isLastCategoryStep && !applyConversion(nextDrafts, helpers)) {
      return;
    }

    setActiveStep(activeStep + 1);
    helpers.setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {t("components.categoryCollectionProfile.actions.convertCategories")}
      </DialogTitle>
      {isLoading ? (
        <DialogContent>
          <div className="flex flex-col gap-4">
            <Skeleton variant="rectangular" className="h-14 w-full rounded" />
          </div>
        </DialogContent>
      ) : (
        <>
          <DialogContent>
            <div className="flex flex-col gap-4">
              <Stepper
                activeStep={activeStep}
                alternativeLabel
                sx={{
                  "& .MuiStep-root": { minWidth: 0 },
                  "& .MuiStepLabel-labelContainer": { maxWidth: "100%" },
                  "& .MuiStepLabel-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <Step>
                  <StepLabel>
                    {t(
                      "components.categoryCollectionProfile.convertCategories.overviewStep"
                    )}
                  </StepLabel>
                </Step>
                {drafts.map((draft) => {
                  const label = translateCategoryName(t, draft.name);
                  return (
                    <Step key={draft.id}>
                      <StepLabel title={label}>{label}</StepLabel>
                    </Step>
                  );
                })}
                <Step>
                  <StepLabel>{t("utils.actions.complete")}</StepLabel>
                </Step>
              </Stepper>

              {isOverview ? (
                <div className="flex flex-col gap-2">
                  <Alert severity="warning">
                    <Trans
                      i18nKey="components.categoryCollectionProfile.warnings.fixedToCustom"
                      components={[<strong key={0} />]}
                    />
                  </Alert>
                  <Typography>
                    {t(
                      "components.categoryCollectionProfile.warnings.fixedToCustomContinue"
                    )}
                  </Typography>
                </div>
              ) : isCompleteStep ? (
                <Alert severity="success">
                  <Typography>
                    {t(
                      "components.categoryCollectionProfile.convertCategories.completeMessage"
                    )}
                  </Typography>
                </Alert>
              ) : (
                currentDraft && (
                  <Formik
                    key={currentDraft.id}
                    initialValues={{
                      name: currentDraft.name,
                      role: currentDraft.role,
                      sourceAlmanacId: currentDraft.sourceAlmanacId,
                      mostSimilarTo: currentDraft.mostSimilarTo,
                    }}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={commitCategoryStep}
                  >
                    {({
                      values,
                      submitForm,
                      isSubmitting,
                      isValid,
                      setFieldValue,
                      setFieldTouched,
                    }) => (
                      <Form className="flex flex-col gap-4">
                        <CreateCategoryFormFields
                          values={values}
                          setFieldValue={setFieldValue}
                          setFieldTouched={setFieldTouched}
                          almanacGroups={almanacGroups}
                          defaultAlmanacId={defaultAlmanacId}
                        />
                        <DialogActions className="px-0 pb-0">
                          <Button
                            variant="text"
                            color="inherit"
                            disabled={isSubmitting}
                            onClick={() => setActiveStep(activeStep - 1)}
                          >
                            {t("utils.actions.backShort")}
                          </Button>
                          <Button
                            variant="text"
                            color="inherit"
                            disabled={isSubmitting}
                            onClick={onClose}
                          >
                            {t("utils.actions.cancel")}
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            loading={isSubmitting}
                            disabled={!isValid}
                            onClick={submitForm}
                          >
                            {isLastCategoryStep
                              ? t("utils.actions.continue")
                              : t("utils.actions.next")}
                          </Button>
                        </DialogActions>
                      </Form>
                    )}
                  </Formik>
                )
              )}
            </div>
          </DialogContent>
          {isOverview && (
            <DialogActions>
              <Button variant="text" color="inherit" onClick={onClose}>
                {t("utils.actions.cancel")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setActiveStep(1)}
                disabled={drafts.length === 0}
              >
                {t("utils.actions.continue")}
              </Button>
            </DialogActions>
          )}
          {isCompleteStep && (
            <DialogActions>
              <Button
                variant="contained"
                color="primary"
                onClick={onClose}
                disabled={!hasConverted}
              >
                {t("utils.actions.done")}
              </Button>
            </DialogActions>
          )}
        </>
      )}
    </Dialog>
  );
};
